import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Card, Col, Form, Row } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import rendezVousService from '../../../services/rendezVousService';
import receptionService from '../../../services/receptionService';
import externalMemberService from '../../../services/externalMemberService';
import RendezVousFormModal from '../forms/RendezVousFormModal';
import RendezVousListRows from '../panels/RendezVousListRows';
import RendezVousRegisterVisitModal from '../panels/RendezVousRegisterVisitModal';
import RendezVousDetailModal from '../panels/RendezVousDetailModal';
import {
  ReceptionPageHeader,
  ReceptionLoadingBlock,
  ReceptionEmptyState
} from '../ui';
import { combineYmdAndTime, parseToInstant } from '../../../utils/appTimezone';
import { buildRdvListQuery, buildRdvExportQuery, RDV_PAGE_SIZE } from '../rdvListQuery';
import { canRegisterRdvVisit } from '../rdvFormatters';
import {
  buildRdvSubmitPayload,
  createEmptyRdvForm,
  rdvPatientLabel,
  rdvToFormState
} from '../rdvFormState';

const PERIOD_OPTIONS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: '7 jours' },
  { value: 'upcoming', label: 'À venir' },
  { value: 'all', label: 'Tous' }
];

export default function ReceptionAppointmentsPage() {
  const { toast, activeReceptionSite } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRdvId, setEditingRdvId] = useState(null);
  const [editingReference, setEditingReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [sites, setSites] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [form, setForm] = useState(createEmptyRdvForm());
  const [periodScope, setPeriodScope] = useState('today');
  const [filters, setFilters] = useState({
    status: '',
    type_rendez_vous: '',
    search: ''
  });
  const [searchDebounced, setSearchDebounced] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [pendingVisitRdv, setPendingVisitRdv] = useState(null);
  const [visitSiteId, setVisitSiteId] = useState('');
  const [visitNote, setVisitNote] = useState('');
  const [registerSaving, setRegisterSaving] = useState(false);
  const [rdvPage, setRdvPage] = useState(0);
  const [detailRdvId, setDetailRdvId] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchDebounced(filters.search);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const listQuery = useMemo(
    () =>
      buildRdvListQuery({
        periodScope,
        status: filters.status,
        type_rendez_vous: filters.type_rendez_vous,
        search: searchDebounced,
        page: rdvPage
      }),
    [periodScope, filters.status, filters.type_rendez_vous, searchDebounced, rdvPage]
  );

  useEffect(() => {
    setRdvPage(0);
  }, [periodScope, filters.status, filters.type_rendez_vous, searchDebounced]);

  const rdvPageCount = useMemo(() => {
    if (!totalCount) return 0;
    return Math.max(1, Math.ceil(totalCount / RDV_PAGE_SIZE));
  }, [totalCount]);

  const rdvRangeLabel = useMemo(() => {
    if (!totalCount) return null;
    const from = rdvPage * RDV_PAGE_SIZE + 1;
    const to = Math.min(totalCount, (rdvPage + 1) * RDV_PAGE_SIZE);
    return `${from}–${to}`;
  }, [rdvPage, totalCount]);

  useEffect(() => {
    if (rdvPageCount > 0 && rdvPage >= rdvPageCount) {
      setRdvPage(Math.max(0, rdvPageCount - 1));
    }
  }, [rdvPage, rdvPageCount]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rendezVousService.list(listQuery);
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotalCount(typeof res.pagination?.total === 'number' ? res.pagination.total : res.data?.length || 0);
      }
    } catch (e) {
      toast('danger', e.message || 'Chargement des rendez-vous impossible');
    } finally {
      setLoading(false);
    }
  }, [listQuery, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const res = await rendezVousService.doctors();
        if (res.success) setDoctors(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setDoctors([]);
      }
    };
    loadDoctors();
  }, []);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const res = await receptionService.getSites();
        if (res.success) setSites(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setSites([]);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    const q = patientSearch.trim();
    if (q.length < 2) {
      setPatientOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setPatientLoading(true);
      try {
        if (form.patient_kind === 'SMIE') {
          const res = await receptionService.searchMembers(q, { limit: 10 });
          setPatientOptions(
            (res.data || []).map((m) => ({
              id: m.id,
              label: `${m.prenom || ''} ${m.nom || ''}`.trim()
            }))
          );
        } else {
          const res = await externalMemberService.list({ search: q, limit: 10, offset: 0 });
          setPatientOptions(
            (res.data || []).map((m) => ({
              id: m.id,
              label: `${m.prenom || ''} ${m.nom || ''}`.trim()
            }))
          );
        }
      } catch (_) {
        setPatientOptions([]);
      } finally {
        setPatientLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [patientSearch, form.patient_kind]);

  const displayRows = useMemo(() => {
    if (quickFilter !== 'to_register') return rows;
    return rows.filter(canRegisterRdvVisit);
  }, [rows, quickFilter]);

  const summary = useMemo(() => {
    const toRegister = rows.filter(canRegisterRdvVisit).length;
    const urgent = rows.filter((r) => String(r.priority || '').toUpperCase() === 'URGENT').length;
    const registered = rows.filter((r) => r.reception_visit_id).length;
    return { toRegister, urgent, registered };
  }, [rows]);

  const resetCreateForm = () => {
    setForm(createEmptyRdvForm());
    setPatientSearch('');
    setPatientOptions([]);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setEditingRdvId(null);
    setEditingReference('');
    resetCreateForm();
  };

  const openCreate = () => {
    setEditingRdvId(null);
    setEditingReference('');
    resetCreateForm();
    setShowFormModal(true);
  };

  const openEdit = useCallback((rdv) => {
    if (!rdv?.id) return;
    setDetailRdvId(null);
    setEditingRdvId(rdv.id);
    setEditingReference(rdv.reference || '');
    setForm(rdvToFormState(rdv));
    setPatientSearch(rdvPatientLabel(rdv));
    setPatientOptions([]);
    setShowFormModal(true);
  }, []);

  const handlePatientSearchChange = (value, patientId) => {
    setPatientSearch(value);
    if (patientId !== undefined) {
      setForm((prev) => ({ ...prev, patient_id: patientId }));
      setPatientOptions([]);
    } else {
      setForm((prev) => ({ ...prev, patient_id: '' }));
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const built = buildRdvSubmitPayload(form, combineYmdAndTime);
    if (built.error) {
      toast('warning', built.error);
      return;
    }
    const { payload } = built;
    if (payload.end_at && parseToInstant(payload.end_at) <= parseToInstant(payload.start_at)) {
      toast('warning', 'Heure de fin doit être supérieure à l’heure de début');
      return;
    }

    setSaving(true);
    try {
      const res = editingRdvId
        ? await rendezVousService.update(editingRdvId, payload)
        : await rendezVousService.create(payload);
      if (res.success) {
        toast('success', editingRdvId ? 'Rendez-vous mis à jour' : 'Rendez-vous créé avec succès');
        const savedId = editingRdvId || res.data?.id;
        closeFormModal();
        await loadData();
        if (savedId) setDetailRdvId(savedId);
      }
    } catch (err) {
      toast('danger', err.message || (editingRdvId ? 'Mise à jour impossible' : 'Création rendez-vous impossible'));
    } finally {
      setSaving(false);
    }
  };

  const submitRegisterVisit = async () => {
    if (!pendingVisitRdv?.id) return;
    if (!visitSiteId) {
      toast('warning', 'Sélectionnez le site de passage');
      return;
    }
    setRegisterSaving(true);
    try {
      const res = await rendezVousService.registerVisit(pendingVisitRdv.id, {
        site_id: parseInt(visitSiteId, 10),
        notes: visitNote.trim() || undefined
      });
      if (res.success) {
        toast(
          'success',
          `Visite #${res.data?.reception_visit_id} créée et orientée (${res.data?.service_line || 'MEDICAL'})`
        );
        setPendingVisitRdv(null);
        setVisitSiteId('');
        setVisitNote('');
        setDetailRdvId(null);
        await loadData();
      }
    } catch (e) {
      toast('danger', e.message || 'Création de visite impossible');
    } finally {
      setRegisterSaving(false);
    }
  };

  const openRegisterVisit = useCallback(
    (rdv) => {
      setDetailRdvId(null);
      setPendingVisitRdv(rdv);
      setVisitSiteId(activeReceptionSite?.id ? String(activeReceptionSite.id) : '');
      setVisitNote('');
    },
    [activeReceptionSite]
  );

  const openDetail = useCallback((rdv) => {
    if (rdv?.id) setDetailRdvId(rdv.id);
  }, []);

  const handleRdvStatusChange = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const handleExportCsv = async () => {
    try {
      const exportQuery = buildRdvExportQuery({
        periodScope,
        status: filters.status,
        type_rendez_vous: filters.type_rendez_vous,
        search: searchDebounced
      });
      await rendezVousService.downloadCsv(exportQuery);
      toast(
        'success',
        `Export CSV téléchargé (${totalCount} RDV filtré${totalCount > 1 ? 's' : ''}).`
      );
    } catch (e) {
      toast('danger', e.message || 'Export CSV impossible');
    }
  };

  const renderRdvPagination = (position = 'bottom') => {
    if (loading || totalCount <= 0 || quickFilter === 'to_register') return null;
    const multiPage = rdvPageCount > 1;
    return (
      <div
        className={`recv-journal-pagination${position === 'top' ? ' mb-3' : ' mt-3'}`}
        aria-label="Pagination des rendez-vous"
      >
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={rdvPage <= 0 || loading}
          onClick={() => setRdvPage((p) => Math.max(0, p - 1))}
        >
          ← Précédent
        </Button>
        <div className="recv-journal-pagination__info">
          Page <strong>{rdvPage + 1}</strong> / {rdvPageCount}
          {' · '}
          <strong>{totalCount}</strong> RDV
          {rdvRangeLabel ? (
            <>
              {' '}
              (lignes {rdvRangeLabel})
            </>
          ) : null}
          {!multiPage ? (
            <span className="d-block small mt-1">Tous les résultats sont sur cette page.</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={rdvPage >= rdvPageCount - 1 || loading}
          onClick={() => setRdvPage((p) => Math.min(rdvPageCount - 1, p + 1))}
        >
          Suivant →
        </Button>
      </div>
    );
  };

  const rdvListHead = (
    <div className="recv-rdv-list__head" aria-hidden="true">
      <span className="recv-rdv-list__cell recv-rdv-list__cell--slot">Créneau</span>
      <span className="recv-rdv-list__cell recv-rdv-list__cell--patient">Patient</span>
      <span className="recv-rdv-list__cell recv-rdv-list__cell--type">Type</span>
      <span className="recv-rdv-list__cell recv-rdv-list__cell--service">Service</span>
      <span className="recv-rdv-list__cell recv-rdv-list__cell--status">Statut</span>
      <span className="recv-rdv-list__cell recv-rdv-list__cell--action">Action</span>
    </div>
  );

  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === periodScope)?.label || 'Période';

  return (
    <div className="recv-dash-pro recv-dash-pro--main">
      <ReceptionPageHeader
        badge="Planification"
        title="Rendez-vous"
        description="Créneaux du jour, enregistrement de passage et suivi des statuts."
        actions={
          <Button variant="primary" onClick={openCreate}>
            Nouveau RDV
          </Button>
        }
      />

      <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-panel-card recv-panel-card--rdv mb-3">
        <Card.Header className="bg-white fw-semibold py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <div className="fw-semibold">Liste des rendez-vous</div>
              <div className="text-muted small fw-normal mt-1">
                {loading ? (
                  'Chargement…'
                ) : (
                  <>
                    <strong>{totalCount}</strong> RDV · période {periodLabel.toLowerCase()}
                    {quickFilter === 'to_register' ? ' · filtre « à enregistrer » sur cette page' : null}
                    {!loading && totalCount > RDV_PAGE_SIZE && quickFilter !== 'to_register' ? (
                      <> · {RDV_PAGE_SIZE} par page</>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            {!loading && rows.length > 0 ? (
              <div className="recv-rdv-summary recv-ops-summary" aria-label="Résumé des rendez-vous">
                <button
                  type="button"
                  className={`recv-ops-summary__item recv-rdv-summary__chip${quickFilter === 'to_register' ? ' is-active' : ''}${summary.toRegister > 0 ? ' is-hot' : ''}`}
                  onClick={() =>
                    setQuickFilter((prev) => (prev === 'to_register' ? '' : 'to_register'))
                  }
                >
                  <span className="recv-ops-summary__value">{summary.toRegister}</span>
                  <span className="recv-ops-summary__label">À enregistrer</span>
                </button>
                <div className="recv-ops-summary__item">
                  <span className="recv-ops-summary__value">{summary.registered}</span>
                  <span className="recv-ops-summary__label">Visites créées</span>
                </div>
                <div className={`recv-ops-summary__item${summary.urgent > 0 ? ' is-hot' : ''}`}>
                  <span className="recv-ops-summary__value">{summary.urgent}</span>
                  <span className="recv-ops-summary__label">Urgents</span>
                </div>
              </div>
            ) : null}
          </div>
        </Card.Header>

        <Card.Body className="pt-3">
          <div className="recv-rdv-search-bar mb-3">
            <Form.Label className="recv-rdv-search-bar__label" htmlFor="recv-rdv-search">
              Rechercher un rendez-vous
            </Form.Label>
            <div className="recv-rdv-search-bar__input-wrap">
              <Form.Control
                id="recv-rdv-search"
                type="search"
                className="recv-rdv-search-bar__input"
                placeholder="Référence, patient, motif…"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
              {filters.search ? (
                <Button
                  variant="link"
                  className="recv-rdv-search-bar__clear"
                  onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}
                >
                  Effacer
                </Button>
              ) : null}
            </div>
            <div className="recv-rdv-search-bar__hint small text-muted mt-1">
              {filters.search.trim() && filters.search !== searchDebounced
                ? 'Recherche en cours…'
                : 'Tapez une référence, un nom de patient ou un motif.'}
            </div>
          </div>

          <Row className="g-2 mb-3 align-items-end flex-wrap">
            <Col xs={12} lg={6}>
              <Form.Label className="small text-muted mb-1 d-block">Période</Form.Label>
              <ButtonGroup size="sm" className="recv-rdv-period">
                {PERIOD_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    variant={periodScope === option.value ? 'primary' : 'outline-primary'}
                    onClick={() => {
                      setPeriodScope(option.value);
                      setQuickFilter('');
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2}>
              <Form.Label className="small text-muted mb-1">Type</Form.Label>
              <Form.Select
                size="sm"
                value={filters.type_rendez_vous}
                onChange={(e) => setFilters((prev) => ({ ...prev, type_rendez_vous: e.target.value }))}
              >
                <option value="">Tous types</option>
                <option value="MEDECIN">Médecin</option>
                <option value="INFIRMERIE">Infirmerie</option>
                <option value="DENTISTE">Dentiste</option>
                <option value="AUTRE_SERVICE">Autre service</option>
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small text-muted mb-1">Statut</Form.Label>
              <Form.Select
                size="sm"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tous statuts</option>
                <option value="EN_ATTENTE">En attente</option>
                <option value="CONFIRME">Confirmé</option>
                <option value="TERMINE">Terminé</option>
                <option value="ANNULE">Annulé</option>
                <option value="ABSENT">Absent</option>
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2} className="d-grid">
              <Button variant="outline-secondary" size="sm" onClick={loadData} disabled={loading}>
                Actualiser
              </Button>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2} className="d-grid">
              <Button
                variant="outline-success"
                size="sm"
                disabled={loading || totalCount === 0 || quickFilter === 'to_register'}
                onClick={handleExportCsv}
              >
                Export CSV
              </Button>
            </Col>
          </Row>

          {loading ? (
            <ReceptionLoadingBlock label="Chargement des rendez-vous…" />
          ) : displayRows.length === 0 ? (
            <ReceptionEmptyState
              title={quickFilter === 'to_register' ? 'Aucun RDV à enregistrer' : 'Aucun rendez-vous'}
              description={
                quickFilter === 'to_register'
                  ? 'Tous les créneaux visibles ont déjà une visite ou sont clôturés.'
                  : 'Ajustez la période ou les filtres, ou créez un nouveau rendez-vous.'
              }
              action={
                quickFilter === 'to_register' ? (
                  <Button variant="outline-primary" size="sm" onClick={() => setQuickFilter('')}>
                    Voir tous les RDV
                  </Button>
                ) : (
                  <Button variant="primary" size="sm" onClick={openCreate}>
                    Créer un RDV
                  </Button>
                )
              }
            />
          ) : (
            <>
              {renderRdvPagination('top')}
              <div className="recv-rdv-list-wrap border rounded bg-white">
                <div className="recv-rdv-list" role="list" aria-label="Liste des rendez-vous">
                  {rdvListHead}
                  <RendezVousListRows
                    rows={displayRows}
                    onRegisterVisit={openRegisterVisit}
                    onViewDetail={openDetail}
                  />
                </div>
              </div>
              {renderRdvPagination('bottom')}
            </>
          )}
        </Card.Body>
      </Card>

      <RendezVousFormModal
        show={showFormModal}
        onHide={closeFormModal}
        saving={saving}
        onSubmit={submitForm}
        mode={editingRdvId ? 'edit' : 'create'}
        editingReference={editingReference}
        lockPatient={!!editingRdvId}
        patientDisplayName={patientSearch}
        form={form}
        setForm={setForm}
        patientSearch={patientSearch}
        onPatientSearchChange={handlePatientSearchChange}
        patientOptions={patientOptions.map((o) => ({
          ...o,
          sub: form.patient_kind === 'SMIE' ? 'SMIE' : 'EXTERNE'
        }))}
        patientLoading={patientLoading}
        doctors={doctors}
        mapServiceOnTypeChange={false}
      />

      <RendezVousDetailModal
        show={!!detailRdvId}
        rdvId={detailRdvId}
        registerDisabled={registerSaving}
        statusDisabled={registerSaving}
        toast={toast}
        onHide={() => setDetailRdvId(null)}
        onRegisterVisit={openRegisterVisit}
        onEdit={openEdit}
        onStatusChange={handleRdvStatusChange}
      />

      <RendezVousRegisterVisitModal
        show={!!pendingVisitRdv}
        rdv={pendingVisitRdv}
        sites={sites}
        visitSiteId={visitSiteId}
        visitNote={visitNote}
        saving={registerSaving}
        activeSiteName={activeReceptionSite?.name}
        onHide={() => !registerSaving && setPendingVisitRdv(null)}
        onSiteChange={setVisitSiteId}
        onNoteChange={setVisitNote}
        onConfirm={submitRegisterVisit}
      />
    </div>
  );
}
